import { SectionHeading } from '@/components/ui'
import type { Insights, Transaction } from '@/modules/projects'
import { CategoryOutliersSection } from './CategoryOutliersSection'
import { outlierReasonsColumn } from './columns'
import { TransactionsAnomalySection } from './TransactionsAnomalySection'
import {
  filterOutlierTransactions,
  filterRoundNumberTransactions,
  filterWeekendTransactions,
  outliersHaveReasons,
  transfersLeftOutLine,
} from './utils'

interface AnomaliesProps {
  slug: string
  insights: Insights
  transactions: Transaction[]
}

export function Anomalies({ slug, insights, transactions }: AnomaliesProps) {
  const { categoryStats, outliers, outlierTransfersLeftOut } = insights
  const leftOutLine = transfersLeftOutLine(outlierTransfersLeftOut)

  return (
    <div className="space-y-6">
      <SectionHeading
        title="Anomalies"
        description="Transactions that deserve a second look: outliers, round-number patterns, and weekend activity."
      />

      {outliersHaveReasons(insights) ? (
        <TransactionsAnomalySection
          slug={slug}
          id="outliers"
          title="Outliers"
          description={
            <>
              The 25 largest amounts in and the 25 largest out, plus any row of
              $10,000 or more to a payee seen at most twice.
              {leftOutLine && <span className="block">{leftOutLine}</span>}
            </>
          }
          emptyMessage="No outliers found."
          filter={filterOutlierTransactions(outliers)}
          transactions={transactions}
          columnsAfterAmount={[outlierReasonsColumn]}
        />
      ) : (
        <CategoryOutliersSection
          categoryStats={categoryStats}
          outliers={outliers}
          transactions={transactions}
        />
      )}

      <TransactionsAnomalySection
        slug={slug}
        id="round-numbers"
        title="Round-number amounts"
        description="Transactions with suspiciously clean amounts (exact multiples of $100), often manual entries, estimates, or transfers."
        emptyMessage="No round-number transactions found."
        filter={filterRoundNumberTransactions}
        transactions={transactions}
      />

      <TransactionsAnomalySection
        slug={slug}
        id="weekend-activity"
        title="Weekend activity"
        description="Transactions dated on a Saturday or Sunday, worth a look when most activity is weekday."
        emptyMessage="No weekend transactions found."
        filter={filterWeekendTransactions}
        transactions={transactions}
      />
    </div>
  )
}
